import { createContext, useState } from 'react';
import { useNavigate, Outlet } from 'react-router-dom';
import { SectionHeader } from '@/components/Section/SectionHeader';
import { SectionSelector } from '@/components/Section/SectionSelector';
import { type Service } from '@/schemas/API/GET/service/response';
import { Stack, type ComboboxItem } from '@mantine/core';
import {useSection} from "@/hooks/user/useSection";

// Creating service context so child elements can access chosen service.
export interface ServiceContextType {
  service: Service | undefined;
  setService: (newService: Service) => void;
}

export const ServiceContext = createContext<ServiceContextType>({
  service: undefined,
  setService: () => {},
});

export interface PersonLayoutProps {
  fullname: string | undefined;
  onClosePath: string;
  serviceDependentSections: string[];
  navigationOptions: ComboboxItem[];
  inputSection: string | undefined;
  checkForServicePresence?: boolean;
  presentServicesId?: number[];
}

/**
 * Generic layout for a person. Person can be client, employee, applicant, ...
 */
export const PersonLayout = ({
  fullname,
  onClosePath,
  serviceDependentSections,
  navigationOptions,
  inputSection,
  checkForServicePresence = false,
  presentServicesId = [],
}: PersonLayoutProps): JSX.Element => {
  const [service, setService] =
      useState<Service | undefined>(undefined);

  const navigate = useNavigate();
  const handleClose = (): void => navigate(`${onClosePath}`);

  const fallbackSection = useSection();
  const section = inputSection ?? fallbackSection;

  // Determine if currently selected layout's section requires service.
  const showServiceSelector: boolean =
    section !== undefined && serviceDependentSections.some(s => s === section);

  return (
    <ServiceContext.Provider value={{ service, setService }}>
      <Stack className="h-full p-8 overflow-auto">
        <SectionHeader
          header={fullname}
          onClose={handleClose}
          showServiceSelector={showServiceSelector}
          checkForServicePresence={checkForServicePresence}
          presentServicesId={presentServicesId}
        />
        <SectionSelector
          navigationOptions={navigationOptions}
          sectionUrlIndex={2}
        />
        <Outlet />
      </Stack>
    </ServiceContext.Provider>
  );
};



